import { useQuery } from '@tanstack/react-query'
import { featureFlagsService } from '@/services/feature-flags'
import { useAuthStore } from '@/stores/auth'

export interface UseFeatureFlagResult {
  loading: boolean
  enabled: boolean
  metadata: Record<string, unknown>
  allowedUsers: string[]
  allowedForCurrentUser: boolean
  refetch: () => void
}

function extractAllowedUsers(metadata: Record<string, unknown>): string[] {
  const raw = metadata.allowed_users
  if (!Array.isArray(raw)) return []
  return raw.filter((v): v is string => typeof v === 'string')
}

export function useFeatureFlag(key: string): UseFeatureFlagResult {
  const userId = useAuthStore((s) => s.user?.id)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['feature-flag', key],
    queryFn: () => featureFlagsService.getByKey(key),
    staleTime: 60 * 1000,
  })

  const enabled = Boolean(data?.enabled)
  const metadata = data?.metadata ?? {}
  const allowedUsers = extractAllowedUsers(metadata)
  const allowedForCurrentUser =
    enabled && Boolean(userId) && allowedUsers.includes(userId as string)

  return {
    loading: isLoading,
    enabled,
    metadata,
    allowedUsers,
    allowedForCurrentUser,
    refetch: () => refetch(),
  }
}
